import { computed, reactive, type ComputedRef } from 'vue';

import type { UserDashboardGetUserResponseType } from '@magma-app/magma-types/all';

import type {
  HelperCampaignCriteriaViewModel,
  HelperCriteriaAnswers,
  HelperSelectOption,
} from '../types';

function buildInitialAnswers(
  userCriteria: UserDashboardGetUserResponseType['userCriteria'] | undefined
): HelperCriteriaAnswers {
  const answers: Record<number, string | string[]> = {};

  if (!userCriteria) {
    return answers;
  }

  for (const criteria of userCriteria) {
    answers[criteria.criteriaId] =
      criteria.answerKeys || criteria.answerKey || '';
  }

  return answers;
}

export function useHelperSignupCriteria(params: {
  campaignCriteria: ComputedRef<HelperCampaignCriteriaViewModel[]>;
  userCriteria: UserDashboardGetUserResponseType['userCriteria'] | undefined;
}) {
  const criteriaAnswers = reactive<HelperCriteriaAnswers>(
    buildInitialAnswers(params.userCriteria)
  );

  const strongCriteriaIds = computed(() =>
    params.campaignCriteria.value
      .filter((criteria) => criteria.weight === 'strong')
      .map((criteria) => criteria.criteriaId)
  );

  const hasStrongCriteriaSelected = computed(() => {
    if (strongCriteriaIds.value.length === 0) {
      return true;
    }

    return strongCriteriaIds.value.every((criteriaId) => {
      const answer = criteriaAnswers[criteriaId];
      if (Array.isArray(answer)) {
        return answer.length > 0;
      }
      return Boolean(answer);
    });
  });

  const getCriteriaOptions = (criteriaId: number): HelperSelectOption[] => {
    const criteria = params.campaignCriteria.value.find(
      (item) => item.criteriaId === criteriaId
    );

    if (!criteria) {
      return [];
    }

    return criteria.options.map((option) => ({
      label: option.value,
      value: option.key,
    }));
  };

  const buildUserCriteriaPayload = () => {
    const campaignCriteriaIds = params.campaignCriteria.value.map(
      (criteria) => criteria.criteriaId
    );

    return Object.entries(criteriaAnswers)
      .map(([criteriaId, answer]) => ({
        criteriaId: Number(criteriaId),
        answer,
      }))
      .filter(({ criteriaId, answer }) => {
        if (!campaignCriteriaIds.includes(criteriaId)) {
          return false;
        }

        if (Array.isArray(answer)) {
          return answer.length > 0;
        }

        return Boolean(answer);
      })
      .map(({ criteriaId, answer }) => {
        if (Array.isArray(answer)) {
          return {
            criteriaId,
            answerKeys: answer,
          };
        }

        return {
          criteriaId,
          answerKey: answer,
        };
      });
  };

  return {
    criteriaAnswers,
    strongCriteriaIds,
    hasStrongCriteriaSelected,
    getCriteriaOptions,
    buildUserCriteriaPayload,
  };
}
